import React, { useCallback, useMemo, useState } from 'react';
import { useComputedColorScheme, useMantineTheme } from '@mantine/core';
import { Group as VisxGroup } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { scaleLinear } from '@visx/scale';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { LinePath } from '@visx/shape';
import type { CustomerCashPoint } from '../../pricing/venuePricingSweep';

export type XYPoint = { x: number; y: number | null };

type InfeasibleBand = { from: number; to: number };

function formatAxisCurrency(n: number): string {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}k`;
    return `${sign}$${Math.round(abs)}`;
}

function finiteYs(points: XYPoint[]): number[] {
    return points.map(p => p.y).filter((y): y is number => y != null && Number.isFinite(y));
}

function yDomain(values: number[], includeZero: boolean): [number, number] {
    if (values.length === 0) return includeZero ? [-1, 1] : [0, 1];
    let min = Math.min(...values);
    let max = Math.max(...values);
    if (includeZero) {
        min = Math.min(0, min);
        max = Math.max(0, max);
    }
    if (min === max) {
        min -= 1;
        max += 1;
    }
    const pad = (max - min) * 0.08;
    return [min - pad, max + pad];
}

type SparklineProps = {
    points: XYPoint[];
    currentX: number;
    selected?: boolean;
    currentInfeasible?: boolean;
    infeasibleRanges?: InfeasibleBand[];
    onSelect?: () => void;
    ariaLabel: string;
};

export const SensitivitySparkline: React.FC<SparklineProps> = ({
    points,
    currentX,
    selected,
    currentInfeasible,
    infeasibleRanges,
    onSelect,
    ariaLabel,
}) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const lineColor = selected ? theme.colors.indigo[6] : theme.colors.teal[6];
    const markerColor = currentInfeasible ? theme.colors.yellow[6] : theme.colors.indigo[6];
    const bandColor = colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4];
    const gridColor = colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];

    return (
        <div style={{ height: 64, width: '100%' }}>
            <ParentSize>
                {({ width, height }) => {
                    if (width < 10 || height < 10) return null;
                    const ys = finiteYs(points);
                    if (ys.length < 2) return null;
                    const xs = points.map(p => p.x);
                    const xScale = scaleLinear<number>({
                        domain: [Math.min(...xs), Math.max(...xs)],
                        range: [4, width - 4],
                    });
                    const yScale = scaleLinear<number>({
                        domain: yDomain(ys, true),
                        range: [height - 6, 6],
                    });
                    const current = points.reduce((best, p) =>
                        Math.abs(p.x - currentX) < Math.abs(best.x - currentX) ? p : best
                    );
                    return (
                        <svg
                            width={width}
                            height={height}
                            role="img"
                            aria-label={ariaLabel}
                            style={{ cursor: onSelect ? 'pointer' : 'default' }}
                            onClick={onSelect}
                        >
                            <line
                                x1={4}
                                x2={width - 4}
                                y1={yScale(0)}
                                y2={yScale(0)}
                                stroke={gridColor}
                                strokeDasharray="3 3"
                            />
                            {(infeasibleRanges ?? []).map((r, i) => (
                                <rect
                                    key={`inf-${i}`}
                                    x={xScale(r.from)}
                                    y={0}
                                    width={Math.max(1, xScale(r.to) - xScale(r.from))}
                                    height={height}
                                    fill={bandColor}
                                    opacity={0.35}
                                />
                            ))}
                            <LinePath<XYPoint>
                                data={points}
                                defined={d => d.y != null}
                                x={d => xScale(d.x)}
                                y={d => yScale(d.y ?? 0)}
                                stroke={lineColor}
                                strokeWidth={selected ? 2.5 : 2}
                            />
                            {current.y != null && (
                                <circle
                                    cx={xScale(current.x)}
                                    cy={yScale(current.y)}
                                    r={3.5}
                                    fill={markerColor}
                                />
                            )}
                        </svg>
                    );
                }}
            </ParentSize>
        </div>
    );
};

type DetailChartProps = {
    points: XYPoint[];
    currentX: number;
    currentInfeasible?: boolean;
    infeasibleRanges?: InfeasibleBand[];
    xLabel: string;
    yLabel: string;
    formatX: (n: number) => string;
    formatY: (n: number) => string;
    yIsCurrency?: boolean;
    onPickX?: (x: number) => void;
    pickAllowed?: (x: number) => boolean;
};

export const SensitivityDetailChart: React.FC<DetailChartProps> = ({
    points,
    currentX,
    currentInfeasible,
    infeasibleRanges,
    xLabel,
    yLabel,
    formatX,
    formatY,
    yIsCurrency = true,
    onPickX,
    pickAllowed,
}) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const lineColor = theme.colors.teal[6];
    const markerColor = currentInfeasible ? theme.colors.yellow[6] : theme.colors.indigo[6];
    const bandColor = colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[4];
    const zeroColor = colorScheme === 'dark' ? theme.colors.dark[3] : theme.colors.gray[5];
    const axisColor = colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6];
    const [hover, setHover] = useState<{
        x: number;
        y: number;
        labelX: number;
        labelY: number;
        allowed: boolean;
    } | null>(null);

    const margin = { top: 16, right: 16, bottom: 40, left: 56 };

    const nearest = useCallback(
        (xVal: number) =>
            points.reduce((best, p) => (Math.abs(p.x - xVal) < Math.abs(best.x - xVal) ? p : best)),
        [points]
    );

    return (
        <div style={{ height: 220, width: '100%', position: 'relative' }}>
            <ParentSize>
                {({ width, height }) => {
                    if (width < 10 || height < 10) return null;
                    const innerW = width - margin.left - margin.right;
                    const innerH = height - margin.top - margin.bottom;
                    const ys = finiteYs(points);
                    if (ys.length < 2) {
                        return (
                            <text x={16} y={height / 2} fill={axisColor} fontSize={12}>
                                No curve for this input
                            </text>
                        );
                    }
                    const xs = points.map(p => p.x);
                    const xScale = scaleLinear<number>({
                        domain: [Math.min(...xs), Math.max(...xs)],
                        range: [0, innerW],
                    });
                    const yScale = scaleLinear<number>({
                        domain: yDomain(ys, true),
                        range: [innerH, 0],
                        nice: true,
                    });
                    const current = nearest(currentX);

                    const handleMove = (event: React.MouseEvent<SVGSVGElement>) => {
                        const rect = event.currentTarget.getBoundingClientRect();
                        const px = event.clientX - rect.left - margin.left;
                        const xVal = xScale.invert(Math.min(innerW, Math.max(0, px)));
                        const p = nearest(xVal);
                        if (p.y == null) {
                            setHover(null);
                            return;
                        }
                        setHover({
                            x: xScale(p.x) + margin.left,
                            y: yScale(p.y) + margin.top,
                            labelX: p.x,
                            labelY: p.y,
                            allowed: pickAllowed ? pickAllowed(p.x) : true,
                        });
                    };

                    const handleClick = () => {
                        if (!onPickX || hover == null) return;
                        if (pickAllowed && !pickAllowed(hover.labelX)) return;
                        onPickX(hover.labelX);
                    };

                    return (
                        <svg
                            width={width}
                            height={height}
                            onMouseMove={handleMove}
                            onMouseLeave={() => setHover(null)}
                            onClick={handleClick}
                            style={{
                                cursor: onPickX
                                    ? hover && hover.allowed === false
                                        ? 'not-allowed'
                                        : 'crosshair'
                                    : 'default',
                            }}
                            role="img"
                            aria-label={yLabel}
                        >
                            <VisxGroup left={margin.left} top={margin.top}>
                                <line
                                    x1={0}
                                    x2={innerW}
                                    y1={yScale(0)}
                                    y2={yScale(0)}
                                    stroke={zeroColor}
                                    strokeDasharray="4 3"
                                />
                                {(infeasibleRanges ?? []).map((r, i) => (
                                    <rect
                                        key={`inf-${i}`}
                                        x={xScale(r.from)}
                                        y={0}
                                        width={Math.max(1, xScale(r.to) - xScale(r.from))}
                                        height={innerH}
                                        fill={bandColor}
                                        opacity={0.28}
                                    />
                                ))}
                                <line
                                    x1={xScale(current.x)}
                                    x2={xScale(current.x)}
                                    y1={0}
                                    y2={innerH}
                                    stroke={markerColor}
                                    strokeDasharray="3 3"
                                    opacity={0.7}
                                />
                                <LinePath<XYPoint>
                                    data={points}
                                    defined={d => d.y != null}
                                    x={d => xScale(d.x)}
                                    y={d => yScale(d.y ?? 0)}
                                    stroke={lineColor}
                                    strokeWidth={2.25}
                                />
                                {current.y != null && (
                                    <circle
                                        cx={xScale(current.x)}
                                        cy={yScale(current.y)}
                                        r={4}
                                        fill={markerColor}
                                        stroke="white"
                                        strokeWidth={1.5}
                                    />
                                )}
                                <AxisLeft
                                    scale={yScale}
                                    numTicks={4}
                                    stroke={axisColor}
                                    tickStroke={axisColor}
                                    tickFormat={v =>
                                        yIsCurrency
                                            ? formatAxisCurrency(v as number)
                                            : String(Math.round(v as number))
                                    }
                                    tickLabelProps={() => ({
                                        fill: axisColor,
                                        fontSize: 10,
                                        textAnchor: 'end',
                                        dx: '-0.25em',
                                        dy: '0.25em',
                                    })}
                                />
                                <AxisBottom
                                    top={innerH}
                                    scale={xScale}
                                    numTicks={Math.max(2, Math.floor(innerW / 72))}
                                    stroke={axisColor}
                                    tickStroke={axisColor}
                                    tickFormat={v => formatX(v as number)}
                                    tickLabelProps={() => ({
                                        fill: axisColor,
                                        fontSize: 10,
                                        textAnchor: 'middle',
                                    })}
                                    label={xLabel}
                                    labelProps={{
                                        fill: axisColor,
                                        fontSize: 11,
                                        textAnchor: 'middle',
                                    }}
                                />
                            </VisxGroup>
                            {hover && (
                                <g>
                                    <circle cx={hover.x} cy={hover.y} r={3.5} fill={lineColor} />
                                    <text
                                        x={Math.min(width - 8, hover.x + 8)}
                                        y={Math.max(14, hover.y - 8)}
                                        fill={axisColor}
                                        fontSize={11}
                                        fontWeight={600}
                                    >
                                        {formatX(hover.labelX)} · {formatY(hover.labelY)}
                                        {!hover.allowed ? ' · outside goals' : ''}
                                    </text>
                                </g>
                            )}
                        </svg>
                    );
                }}
            </ParentSize>
        </div>
    );
};

type TrajectoryProps = {
    data: CustomerCashPoint[];
    conversionMonth: number;
};

export const CustomerCashTrajectoryChart: React.FC<TrajectoryProps> = ({ data, conversionMonth }) => {
    const theme = useMantineTheme();
    const colorScheme = useComputedColorScheme('light');
    const oneTimeColor = theme.colors.indigo[6];
    const subColor = theme.colors.teal[6];
    const totalColor = colorScheme === 'dark' ? theme.colors.gray[3] : theme.colors.dark[4];
    const axisColor = colorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6];
    const markerColor = theme.colors.gray[5];
    const margin = { top: 12, right: 16, bottom: 36, left: 56 };

    const series = useMemo(
        () => [
            { key: 'oneTime' as const, label: 'Cumulative one-time', color: oneTimeColor },
            { key: 'subscription' as const, label: 'Cumulative subscription', color: subColor },
            { key: 'total' as const, label: 'Cumulative total', color: totalColor },
        ],
        [oneTimeColor, subColor, totalColor]
    );

    return (
        <div>
        <div style={{ height: 280, width: '100%' }}>
            <ParentSize>
                {({ width, height }) => {
                    if (width < 10 || height < 10 || data.length < 2) return null;
                    const innerW = width - margin.left - margin.right;
                    const innerH = height - margin.top - margin.bottom;
                    const xMax = data[data.length - 1].month;
                    const yValues = data.flatMap(d => [
                        d.cumulativeOneTime,
                        d.cumulativeSubscription,
                        d.cumulativeTotal,
                    ]);
                    const xScale = scaleLinear<number>({ domain: [0, xMax], range: [0, innerW] });
                    const yScale = scaleLinear<number>({
                        domain: yDomain(yValues, true),
                        range: [innerH, 0],
                        nice: true,
                    });

                    const markers = [0, conversionMonth, 12, 24].filter(
                        (m, i, arr) => m <= xMax && arr.indexOf(m) === i
                    );

                    return (
                        <svg width={width} height={height} role="img" aria-label="Customer cash over 24 months">
                            <VisxGroup left={margin.left} top={margin.top}>
                                {markers.map(m => (
                                    <g key={m}>
                                        <line
                                            x1={xScale(m)}
                                            x2={xScale(m)}
                                            y1={0}
                                            y2={innerH}
                                            stroke={markerColor}
                                            strokeDasharray="3 3"
                                            opacity={0.6}
                                        />
                                        <text
                                            x={xScale(m) + 4}
                                            y={12}
                                            fill={axisColor}
                                            fontSize={10}
                                        >
                                            {m === 0
                                                ? 'Signup'
                                                : m === conversionMonth && conversionMonth > 0
                                                  ? 'Convert'
                                                  : `${m / 12}y`}
                                        </text>
                                    </g>
                                ))}
                                <LinePath<CustomerCashPoint>
                                    data={data}
                                    x={d => xScale(d.month)}
                                    y={d => yScale(d.cumulativeOneTime)}
                                    stroke={oneTimeColor}
                                    strokeWidth={2}
                                />
                                <LinePath<CustomerCashPoint>
                                    data={data}
                                    x={d => xScale(d.month)}
                                    y={d => yScale(d.cumulativeSubscription)}
                                    stroke={subColor}
                                    strokeWidth={2}
                                />
                                <LinePath<CustomerCashPoint>
                                    data={data}
                                    x={d => xScale(d.month)}
                                    y={d => yScale(d.cumulativeTotal)}
                                    stroke={totalColor}
                                    strokeWidth={2.25}
                                />
                                <AxisLeft
                                    scale={yScale}
                                    numTicks={5}
                                    stroke={axisColor}
                                    tickStroke={axisColor}
                                    tickFormat={v => formatAxisCurrency(v as number)}
                                    tickLabelProps={() => ({
                                        fill: axisColor,
                                        fontSize: 10,
                                        textAnchor: 'end',
                                        dx: '-0.25em',
                                        dy: '0.25em',
                                    })}
                                />
                                <AxisBottom
                                    top={innerH}
                                    scale={xScale}
                                    numTicks={Math.max(2, Math.floor(innerW / 80))}
                                    stroke={axisColor}
                                    tickStroke={axisColor}
                                    tickFormat={v => `${v}mo`}
                                    tickLabelProps={() => ({
                                        fill: axisColor,
                                        fontSize: 10,
                                        textAnchor: 'middle',
                                    })}
                                />
                            </VisxGroup>
                        </svg>
                    );
                }}
            </ParentSize>
        </div>
            <div
                style={{
                    display: 'flex',
                    gap: 16,
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    marginTop: 8,
                }}
            >
                {series.map(s => (
                    <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                        <span
                            style={{
                                width: 12,
                                height: 2,
                                background: s.color,
                                display: 'inline-block',
                            }}
                        />
                        {s.label}
                    </span>
                ))}
            </div>
        </div>
    );
};
